import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
// Controllers
import { AlertController, LoadingController } from '@ionic/angular';
// Components
import { VehicleListComponent } from '../vehicle-list/vehicle-list.component';
import { PilotListComponent } from '../pilot-list/pilot-list.component';
// Models
import { User, DroneOperator, Vehicle, Pilot } from '@mocca-technologies/model';
// Services
import { DroneOperatorService } from 'src/app/services/drone-operator.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { FilesService } from 'src/app/services/files.service';
import { HttpResponsesService } from 'src/app/services/httpResponses.service';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { LoadingGuardService } from 'src/app/services/loading-guard.service';
import { HttpErrorResponse } from '@angular/common/http';


@Component({
  selector: 'app-sub-operators-list-card',
  templateUrl: './sub-operators-list-card.component.html',
  styleUrls: ['./sub-operators-list-card.component.scss'],
})
export class SubOperatorsListCardComponent implements OnInit, OnDestroy {
  /**
   * Sub-operators provided by parent (petition once)
   */
  @Input() subOperators: DroneOperator[] = [];

  /**
   * Selected sub-operator (for toggling selection)
   */
  @Input() selectedOperator: DroneOperator;

  /**
   * Map of sub-operator pilots/vehicles (petition once)
   */
  @Input() subOperatorPilotsMap: Record<string, Pilot[]> = {};
  @Input() subOperatorVehiclesMap: Record<string, Vehicle[]> = {};

  /**
   * Selection output
   */
  @Output() selectOperatorGlobally = new EventEmitter<DroneOperator>();

  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private filesService: FilesService,
    private httpResponseService: HttpResponsesService,
    private translateService: TranslateService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private droneOperatorService: DroneOperatorService,
    private refreshService: RefreshService,
  ) {}

  ngOnInit(): void {
    // No loading here: parent provides everything
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.selectedOperator?.avatarUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(this.selectedOperator.avatarUrl);
    }
  }

  /**
   * Select / deselect sub-operator. Keeps existing UX.
   * Also attempts to load the logo blob (feature preserved).
   */
  selectOperator(operator: DroneOperator): void {
    if (!operator) {
      this.selectedOperator = null;
      this.selectOperatorGlobally.emit(null);
      this.cdr.detectChanges();
      return;
    }

    if (operator?.uniqueId === this.selectedOperator?.uniqueId) {
      this.selectedOperator = null;
      this.selectOperatorGlobally.emit(null);
      this.cdr.detectChanges();
      return;
    }

    this.filesService
      .getDOLogo(operator?.uniqueId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob: Blob) => {
          if (!blob || blob.size === 0) {
            operator.avatarUrl = null;
          } else {
            if (operator.avatarUrl?.startsWith('blob:')) {
              URL.revokeObjectURL(operator.avatarUrl);
            }
            operator.avatarUrl = URL.createObjectURL(blob);
          }

          this.selectedOperator = operator;
          this.selectOperatorGlobally.emit(operator);
          this.cdr.detectChanges();
        },
        error: async (error: any) => {
          // 422/404 means no image -> not an error for UI
          if (error?.status !== 422 && error?.status !== 404) {
            let body = error?.error;
            if (body instanceof Blob) {
              body = await body.text();
              try { body = JSON.parse(body); } catch {}
            }
            this.httpResponseService.showServerError(error?.status, body);
          } else {
            operator.avatarUrl = null;
          }

          this.selectedOperator = operator;
          this.selectOperatorGlobally.emit(operator);
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Navigate to create sub-operator form.
   */
  goToOperatorsForm(): void {
    this.router.navigate(['/operator-form'], {
      state: { isEdit: false, operator: null, isSubOperator: true },
    });
  }

  /**
   * Navigate to edit sub-operator form.
   */
  editSubOperator(operator: DroneOperator): void {
    this.router.navigate(['/operator-form'], {
      state: { operator, isEdit: true, isSubOperator: true },
    });
  }

  /**
   * Delete sub-operator confirmation placeholder.
   * Kept for feature parity; actual deletion likely handled elsewhere in your original TS.
   */
  async deleteSubOperator(operator: DroneOperator): Promise<void> {
    const hasVehicles = (this.subOperatorVehiclesMap?.[operator?.uniqueId] || []).length > 0;
    const hasPilots = (this.subOperatorPilotsMap?.[operator?.uniqueId] || []).length > 0;

    let messageKey = 'PAGES.FLEET.DELETE_SUBOPERATOR_MESSAGE';
    if (hasVehicles && hasPilots) messageKey = 'PAGES.FLEET.DELETE_SUBOPERATOR_MESSAGE_WITH_BOTH';
    else if (hasVehicles) messageKey = 'PAGES.FLEET.DELETE_SUBOPERATOR_MESSAGE_WITH_VEHICLES';
    else if (hasPilots) messageKey = 'PAGES.FLEET.DELETE_SUBOPERATOR_MESSAGE_WITH_PILOTS';

    const alert = await this.alertController.create({
      header: this.translateService.instant('PAGES.FLEET.DELETE_SUBOPERATOR_TITLE'),
      message: this.translateService.instant(messageKey, { name: operator?.organizationName }),
      buttons: [
        { text: this.translateService.instant('COMMON.BUTTONS.CANCEL'), role: 'cancel' },
        {
          text: this.translateService.instant('COMMON.BUTTONS.CONFIRM'),
          handler: async () => {
            const loading = await this.loadingController.create({ spinner: 'bubbles' });
            await loading.present();

            this.droneOperatorService.deleteSubOperator(operator.uniqueId).subscribe({
              next: async () => {
                await loading.dismiss();
                this.refreshService.triggerRefresh('fleet');
              },
              error: async (err: HttpErrorResponse) => {
                await loading.dismiss();
                this.httpResponseService.showServerError(err?.status, err?.error);
              },
            });
          },
        },
      ],
    });

    await alert.present();
  }

  /**
   * Helper: pilots list for a given operator id
   */
  getPilotsFor(opId: string): Pilot[] {
    return this.subOperatorPilotsMap?.[opId] || [];
  }

  /**
   * Helper: vehicles list for a given operator id
   */
  getVehiclesFor(opId: string): Vehicle[] {
    return this.subOperatorVehiclesMap?.[opId] || [];
  }
}
